import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Bell, Trash2, Search as SearchIcon } from "lucide-react";

/**
 * SearchAlertsPage — iter88.2 · User-Enhancement
 * Zeigt gespeicherte Suchen des Users. Ein Klick öffnet den Marketplace
 * mit den gespeicherten Filtern. Löschen entfernt den Alert.
 */
export default function SearchAlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState("MARKETPLACE");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/search-alerts");
      setAlerts(data.alerts || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/search-alerts/${id}`);
      setAlerts(prev => prev.filter(a => a.alert_id !== id));
    } catch { /* noop */ }
  };

  const openAlert = (a) => {
    const p = new URLSearchParams();
    if (a.filters?.category) p.set("category", a.filters.category);
    if (a.filters?.subcategory) p.set("subcategory", a.filters.subcategory);
    if (a.filters?.size) p.set("size", a.filters.size);
    if (a.filters?.color) p.set("color", a.filters.color);
    if (a.filters?.material) p.set("material", a.filters.material);
    if (a.filters?.condition) p.set("condition", a.filters.condition);
    const base = (a.area_scope === "STILLTOUCHEDX") ? "/stilltouchedx" : "/marketplace";
    navigate(`${base}?${p.toString()}`);
  };

  const visible = alerts.filter((a) => (a.area_scope || "MARKETPLACE") === area);

  return (
    <div data-testid="search-alerts-page" className="bg-white min-h-full">
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button data-testid="search-alerts-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-lg font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2">
            <Bell size={16} /> Gespeicherte Suchen
          </h1>
          <p className="text-xs text-[#999]">Erhalte Push-Benachrichtigungen bei neuen Treffern.</p>
        </div>
      </div>

      {/* Bereichs-Untertabs: Marketplace / stilltouchedX */}
      <div data-testid="search-alerts-area-tabs" className="flex gap-1 p-1 mx-4 mt-1 mb-2 rounded-full bg-[#F0F0F0] w-fit">
        {[{ k: "MARKETPLACE", l: "Marketplace" }, { k: "STILLTOUCHEDX", l: "stilltouchedX" }].map((tab) => (
          <button key={tab.k} data-testid={`search-alerts-tab-${tab.k.toLowerCase()}`} onClick={() => setArea(tab.k)}
            className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${area === tab.k ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:text-[#0A0A0A]"}`}>
            {tab.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="px-4 py-16 text-center text-sm text-[#999]">
          Noch keine gespeicherte Suche in diesem Bereich.<br />
          Öffne {area === "STILLTOUCHEDX" ? "stilltouchedX" : "den Marketplace"}, filtere und tippe auf „Suche speichern".
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {visible.map((a) => (
            <div key={a.alert_id} data-testid={`search-alert-${a.alert_id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-[#FAFAFA]">
              <button onClick={() => openAlert(a)} className="flex-1 text-left flex items-center gap-2">
                <SearchIcon size={14} className="text-[#999] shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{a.label}</p>
                  <p className="text-[10px] text-[#999]">
                    {a.match_count || 0} Treffer bisher
                    {a.last_match_at && ` · zuletzt ${new Date(a.last_match_at).toLocaleDateString("de-DE")}`}
                  </p>
                </div>
              </button>
              <button data-testid={`search-alert-delete-${a.alert_id}`}
                onClick={() => remove(a.alert_id)}
                className="p-2 text-red-500 hover:bg-red-50">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
